import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import {Heart, Loader2, Search, ChevronDown, Eye, Check, X, FileText, Download, Filter, RefreshCw, TrendingUp, Clock, CheckCircle, XCircle, Trash2, RotateCcw, Calendar, AlertTriangle, } from 'lucide-react'
import toast from 'react-hot-toast'
import type { Donation } from '@/types'
import { useAuth, canEdit, canDelete } from '@/hooks/useAuth'

const BLUE    = '#1A56DB'
const BLUE_BG = '#EFF6FF'
const GOLD    = '#C8922A'

type StatusFilter = 'all' | 'pending' | 'confirmed' | 'rejected'
type ActiveTab    = 'list' | 'trash'

const STATUS_LABEL: Record<string, string> = {
  pending:   'Pendente',
  confirmed: 'Confirmada',
  rejected:  'Rejeitada',
  deleted:   'Excluída',
}

const STATUS_COLOR: Record<string, { bg: string; text: string }> = {
  pending:   { bg: '#fff8f0', text: GOLD },
  confirmed: { bg: BLUE_BG,  text: BLUE },
  rejected:  { bg: '#fef2f2', text: '#ef4444' },
  deleted:   { bg: '#f3f4f6', text: '#6b7280' },
}

function fmtCurrency(v: number) {
  return v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || '—'
}

function fmtDate(iso: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function maskCpf(cpf: string) {
  if (!cpf) return '—'
  const d = cpf.replace(/\D/g, '')
  if (d.length !== 11) return cpf
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

// Formata data para input type="date" (YYYY-MM-DD)
function toInputDate(iso: string) {
  if (!iso) return ''
  return iso.slice(0, 10)
}

// Data máxima = hoje
function todayStr() {
  return new Date().toISOString().slice(0, 10)
}

export default function ManageDonations() {
  const { user } = useAuth()
  const allowEdit   = canEdit(user, 'doacoes')
  const allowDelete = canDelete(user, 'doacoes')

  const [tab, setTab]               = useState<ActiveTab>('list')
  const [donations, setDonations]   = useState<Donation[]>([])
  const [trash, setTrash]           = useState<Donation[]>([])
  const [loading, setLoading]       = useState(true)
  const [loadingTrash, setLoadingTrash] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [search, setSearch]         = useState('')
  const [selected, setSelected]     = useState<Donation | null>(null)
  const [updating, setUpdating]     = useState<string | null>(null)

  // Filtro de período
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo]     = useState('')

  // ── Busca lista principal (exceto deletadas) ──
  const fetchDonations = useCallback(async () => {
    setLoading(true)
    try {
      const filter: Record<string, unknown> = {
        status: { $ne: 'deleted' },
      }
      if (statusFilter !== 'all') filter.status = statusFilter

      const { list } = await lumi.entities.donations.list({
        filter,
        sort: { createdAt: -1 },
        limit: 500,
      })
      setDonations(list as Donation[])
    } catch {
      toast.error('Erro ao carregar doações.')
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  // ── Busca lixeira (deletadas) ──
  const fetchTrash = useCallback(async () => {
    setLoadingTrash(true)
    try {
      const { list } = await lumi.entities.donations.list({
        filter: { status: 'deleted' },
        sort: { deletedAt: -1 },
        limit: 500,
      })
      setTrash(list as Donation[])
    } catch {
      toast.error('Erro ao carregar lixeira.')
    } finally {
      setLoadingTrash(false)
    }
  }, [])

  useEffect(() => { fetchDonations() }, [fetchDonations])
  useEffect(() => { if (tab === 'trash') fetchTrash() }, [tab, fetchTrash])

  // ── Atualizar status ──
  const updateStatus = async (id: string, status: 'confirmed' | 'rejected') => {
    setUpdating(id)
    try {
      await lumi.entities.donations.update(id, {
        status,
        updatedAt: new Date().toISOString(),
      })
      setDonations(prev => prev.map(d => d._id === id ? { ...d, status } : d))
      if (selected?._id === id) setSelected(prev => prev ? { ...prev, status } : prev)
      toast.success(status === 'confirmed' ? 'Doação confirmada!' : 'Doação rejeitada.')
    } catch {
      toast.error('Erro ao atualizar status.')
    } finally {
      setUpdating(null)
    }
  }

  // ── Excluir para lixeira (soft-delete) ──
  const deleteDonation = async (donation: Donation) => {
    if (!allowDelete) return
    const confirmed = window.confirm(
      `Excluir o registro de "${donation.donorName}" (${fmtCurrency(donation.amount)})?\n\nO registro irá para a Lixeira e poderá ser consultado lá.`
    )
    if (!confirmed) return
    setUpdating(donation._id)
    try {
      await lumi.entities.donations.update(donation._id, {
        status:    'deleted',
        deletedBy: user?.displayName || user?.userId || 'admin',
        deletedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      setDonations(prev => prev.filter(d => d._id !== donation._id))
      if (selected?._id === donation._id) setSelected(null)
      toast.success('Registro movido para a lixeira.')
    } catch {
      toast.error('Erro ao excluir registro.')
    } finally {
      setUpdating(null)
    }
  }

  // ── Restaurar da lixeira ──
  const restoreDonation = async (donation: Donation) => {
    setUpdating(donation._id)
    try {
      await lumi.entities.donations.update(donation._id, {
        status:    'pending',
        deletedBy: '',
        deletedAt: '',
        updatedAt: new Date().toISOString(),
      })
      setTrash(prev => prev.filter(d => d._id !== donation._id))
      toast.success('Registro restaurado com sucesso.')
    } catch {
      toast.error('Erro ao restaurar registro.')
    } finally {
      setUpdating(null)
    }
  }

  // ── Filtros na lista principal ──
  const filtered = donations.filter(d => {
    const q = search.toLowerCase()
    const matchSearch = (
      d.donorName?.toLowerCase().includes(q) ||
      d.donorCpf?.includes(q)
    )
    // filtro de período
    let matchPeriod = true
    if (dateFrom) {
      matchPeriod = matchPeriod && d.createdAt >= dateFrom
    }
    if (dateTo) {
      // inclui o dia inteiro do dateTo
      matchPeriod = matchPeriod && d.createdAt <= dateTo + 'T23:59:59'
    }
    return matchSearch && matchPeriod
  })

  // ── Totais ──
  const total          = donations.length
  const totalConfirmed = donations.filter(d => d.status === 'confirmed').length
  const totalPending   = donations.filter(d => d.status === 'pending').length
  const totalValue     = donations
    .filter(d => d.status === 'confirmed')
    .reduce((acc, d) => acc + (Number(d.amount) || 0), 0)

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">

      {/* Cabeçalho */}
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Gestão de Doações</h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
            Acompanhe, confirme e gerencie todas as doações via PIX
          </p>
        </div>
        <button
          onClick={tab === 'list' ? fetchDonations : fetchTrash}
          disabled={loading || loadingTrash}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={(loading || loadingTrash) ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
        {([
          { key: 'list',  label: 'Doações',  icon: Heart },
          { key: 'trash', label: 'Lixeira',  icon: Trash2 },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
            style={tab === key
              ? { background: '#fff', color: BLUE, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }
              : { color: '#6b7280' }
            }
          >
            <Icon size={13} />
            {label}
            {key === 'trash' && trash.length > 0 && (
              <span
                className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                style={{ background: '#ef4444' }}
              >
                {trash.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ══════════════ ABA: DOAÇÕES ══════════════ */}
      {tab === 'list' && (
        <>
          {/* Cards de resumo */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Total de Registos', value: total,          icon: Heart,        color: GOLD,      bg: '#fff8f0' },
              { label: 'Pendentes',         value: totalPending,   icon: Clock,        color: '#e67e22', bg: '#fff8f0' },
              { label: 'Confirmadas',       value: totalConfirmed, icon: CheckCircle,  color: BLUE,      bg: BLUE_BG  },
              { label: 'Valor Confirmado',  value: fmtCurrency(totalValue), icon: TrendingUp, color: '#059669', bg: '#ecfdf5' },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: bg }}>
                  <Icon size={18} style={{ color }} />
                </div>
                <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white leading-tight">{loading ? '—' : value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4 flex-wrap">
            {/* Busca */}
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Pesquisar por nome ou CPF..."
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${BLUE}40`)}
                onBlur={e => (e.currentTarget.style.boxShadow = '')}
              />
            </div>

            {/* Status */}
            <div className="relative">
              <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                className="pl-8 pr-8 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none appearance-none cursor-pointer"
              >
                <option value="all">Todos os status</option>
                <option value="pending">Pendentes</option>
                <option value="confirmed">Confirmadas</option>
                <option value="rejected">Rejeitadas</option>
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Período DE */}
            <div className="relative flex items-center gap-1">
              <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateFrom}
                max={dateTo || todayStr()}
                onChange={e => setDateFrom(e.target.value)}
                className="pl-8 pr-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                title="Data inicial"
              />
            </div>

            {/* Período ATÉ */}
            <div className="relative flex items-center gap-1">
              <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                max={todayStr()}
                onChange={e => {
                  if (e.target.value > todayStr()) {
                    toast.error('Não é possível selecionar uma data futura.')
                    return
                  }
                  setDateTo(e.target.value)
                }}
                className="pl-8 pr-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                title="Data final"
              />
            </div>

            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo('') }}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-500 hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <X size={12} /> Limpar período
              </button>
            )}
          </div>

          {/* Tabela */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 size={24} className="animate-spin" style={{ color: BLUE }} />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center">
                <Heart size={32} className="mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-400">Nenhuma doação encontrada.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800">
                      {['Doador', 'CPF', 'Valor', 'Data', 'Status', 'Ações'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                    {filtered.map(d => {
                      const sc = STATUS_COLOR[d.status] || STATUS_COLOR.pending
                      return (
                        <tr key={d._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                                style={{ background: GOLD }}
                              >
                                {d.donorName?.charAt(0)?.toUpperCase() || 'D'}
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white truncate max-w-[140px]">{d.donorName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs">{maskCpf(d.donorCpf)}</td>
                          <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">{fmtCurrency(d.amount)}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(d.createdAt)}</td>
                          <td className="px-4 py-3">
                            <span
                              className="text-xs px-2.5 py-1 rounded-full font-semibold"
                              style={{ background: sc.bg, color: sc.text }}
                            >
                              {STATUS_LABEL[d.status] || d.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {/* Ver detalhes */}
                              <button
                                onClick={() => setSelected(d)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 transition-all"
                                title="Ver detalhes"
                              >
                                <Eye size={15} />
                              </button>

                              {/* Confirmar / Rejeitar (apenas pendentes) */}
                              {allowEdit && d.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => updateStatus(d._id, 'confirmed')}
                                    disabled={updating === d._id}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950 transition-all disabled:opacity-50"
                                    title="Confirmar doação"
                                  >
                                    {updating === d._id ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                                  </button>
                                  <button
                                    onClick={() => updateStatus(d._id, 'rejected')}
                                    disabled={updating === d._id}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-all disabled:opacity-50"
                                    title="Rejeitar doação"
                                  >
                                    <X size={15} />
                                  </button>
                                </>
                              )}

                              {/* Excluir (move para lixeira) */}
                              {allowDelete && (
                                <button
                                  onClick={() => deleteDonation(d)}
                                  disabled={updating === d._id}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-all disabled:opacity-50"
                                  title="Mover para lixeira"
                                >
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-3 text-right">{filtered.length} registo(s)</p>
        </>
      )}

      {/* ══════════════ ABA: LIXEIRA ══════════════ */}
      {tab === 'trash' && (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 leading-relaxed">
              Registros excluídos aparecem aqui com o nome de quem excluiu e a data. Use "Restaurar" para devolver à lista principal.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
            {loadingTrash ? (
              <div className="flex justify-center py-16">
                <Loader2 size={24} className="animate-spin" style={{ color: BLUE }} />
              </div>
            ) : trash.length === 0 ? (
              <div className="py-16 text-center">
                <Trash2 size={32} className="mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-400">Lixeira vazia.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800">
                      {['Doador', 'Valor', 'Data PIX', 'Excluído por', 'Data Exclusão', 'Ação'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                    {trash.map(d => (
                      <tr key={d._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors opacity-75">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 bg-gray-400">
                              {d.donorName?.charAt(0)?.toUpperCase() || 'D'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-700 dark:text-gray-300 text-sm truncate max-w-[130px]">{d.donorName}</p>
                              <p className="text-xs text-gray-400 font-mono">{maskCpf(d.donorCpf)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">{fmtCurrency(d.amount)}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(d.createdAt)}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-50 text-red-600">
                            {d.deletedBy || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(d.deletedAt || '')}</td>
                        <td className="px-4 py-3">
                          {allowEdit && (
                            <button
                              onClick={() => restoreDonation(d)}
                              disabled={updating === d._id}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all disabled:opacity-50"
                              style={{ borderColor: BLUE, color: BLUE }}
                              onMouseEnter={e => { e.currentTarget.style.background = BLUE_BG }}
                              onMouseLeave={e => { e.currentTarget.style.background = '' }}
                            >
                              {updating === d._id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                              Restaurar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-3 text-right">{trash.length} registo(s) na lixeira</p>
        </>
      )}

      {/* ══ Modal de detalhes ══ */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <Heart size={16} style={{ color: GOLD }} />
                <h3 className="font-bold text-gray-900 dark:text-white">Detalhes da Doação</h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Status badge */}
              <div className="flex justify-center">
                {(() => {
                  const sc = STATUS_COLOR[selected.status] || STATUS_COLOR.pending
                  return (
                    <span
                      className="text-sm px-4 py-1.5 rounded-full font-bold"
                      style={{ background: sc.bg, color: sc.text }}
                    >
                      {STATUS_LABEL[selected.status] || selected.status}
                    </span>
                  )
                })()}
              </div>

              {/* Campos */}
              {[
                { label: 'Nome do Doador', value: selected.donorName },
                { label: 'CPF',   value: maskCpf(selected.donorCpf) },
                { label: 'Valor', value: fmtCurrency(selected.amount) },
                { label: 'Data',  value: fmtDate(selected.createdAt) },
                ...(selected.pixKey ? [{ label: 'Chave PIX', value: selected.pixKey }] : []),
                ...(selected.notes  ? [{ label: 'Observações', value: selected.notes }] : []),
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between gap-3 py-2 border-b border-gray-50 dark:border-gray-800 last:border-0">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
                  <span className="text-sm text-gray-900 dark:text-white font-medium text-right">{value}</span>
                </div>
              ))}

              {/* Comprovante */}
              {selected.receiptUrl && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Comprovante</p>
                  <a
                    href={selected.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <FileText size={15} style={{ color: BLUE }} />
                    Ver comprovante
                    <Download size={13} className="ml-auto text-gray-400" />
                  </a>
                </div>
              )}

              {/* Ações de status (se pendente) */}
              {allowEdit && selected.status === 'pending' && (
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => updateStatus(selected._id, 'rejected')}
                    disabled={!!updating}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                  >
                    <XCircle size={15} />
                    Rejeitar
                  </button>
                  <button
                    onClick={() => updateStatus(selected._id, 'confirmed')}
                    disabled={!!updating}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                    style={{ background: BLUE }}
                  >
                    {updating === selected._id ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                    Confirmar
                  </button>
                </div>
              )}

              {/* Excluir do modal */}
              {allowDelete && selected.status !== 'deleted' && (
                <div className="pt-1">
                  <button
                    onClick={() => { deleteDonation(selected) }}
                    disabled={!!updating}
                    className="w-full py-2 rounded-xl text-xs font-semibold border border-red-100 text-red-400 hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                  >
                    <Trash2 size={13} />
                    Mover para lixeira
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
